/**
 * SyncedIndicator — small "synced 4s ago" badge that ticks once a
 * second. Renders nothing if no sync has happened yet. Used by Phase
 * 3b pages (AgentInventory, OpportunityRadar) to turn the silent REST
 * refetch into a visible confidence signal.
 */
import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

function formatRelative(secs) {
  if (secs < 5) return "synced just now";
  if (secs < 60) return `synced ${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `synced ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `synced ${hours}h ago`;
}

export function SyncedIndicator({ lastSyncedAt, loading, onRefetch, testId = "synced-indicator" }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!lastSyncedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  if (!lastSyncedAt && !loading) return null;
  const secs = lastSyncedAt ? Math.max(0, Math.floor((now - lastSyncedAt.getTime()) / 1000)) : 0;
  const label = loading
    ? "syncing…"
    : lastSyncedAt
      ? formatRelative(secs)
      : null;

  return (
    <button
      type="button"
      onClick={onRefetch}
      disabled={loading}
      className={cn(
        "inline-flex items-center gap-1.5 text-meta text-ink-secondary",
        "hover:text-tralenex-700 transition-colors",
        loading && "cursor-wait opacity-70",
      )}
      title="Click to re-sync from the server"
      data-testid={testId}
    >
      <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
      <span className="tabular-nums">{label}</span>
    </button>
  );
}
